import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import React, { useEffect } from "react";
import {
  formatSessionNameForURL,
  isWindowInIframe,
  replaceDashesWithSpaces,
  setGalleryNavBackButtonUrl,
} from "../../v2_actions/nav";

import DesktopEventList from "./DesktopEventList";
import DesktopEventPageFilter from "./DesktopEventPageFilter";
import { EventsLayout } from "../layout/EventsLayout";
import LoadingSpinner from "../../views/components/LoadingSpinner";
import MobileEventList from "../MobileEventList";
import ReactGA from "react-ga4";
import SubEventFilter from "../SubEventFilter";
import { useEventsPageStartupEffects } from "../hooks/useEventsPageStartupEffects";
import { useIsMobile, TOP_PADDING_PX, VIEWPORT_HEIGHT } from "../../actions/ui";
import { useParams } from "react-router-dom";

export const EventsPage = () => {
  setGalleryNavBackButtonUrl();
  const isMobile = useIsMobile();
  const isInIframe = isWindowInIframe();
  const { album_name, gallery_event_name, publicUrlPathForGalleryEvent } =
    useParams();

  const albumNameForAPI = replaceDashesWithSpaces(album_name);
  const galleryEventNameForAPI = replaceDashesWithSpaces(gallery_event_name);
  const galleryEventNameForURL = formatSessionNameForURL(gallery_event_name);

  const isLoadingStaticData = useEventsPageStartupEffects();

  useEffect(() => {
    if (gallery_event_name) {
      ReactGA.event({
        category: "Events/Schools",
        label: `${gallery_event_name}`,
        action: "Entered an events/school page",
      });
    }
    return () => {
      if (gallery_event_name) {
        ReactGA.event({
          category: "Events/Schools",
          label: `${gallery_event_name}`,
          action: "Exited an events/school page",
        });
      }
    };
  }, [gallery_event_name]);

  // --- Pin ONLY the map preview card (overlayed; desktop only) --------------
  useEffect(() => {
    if (isMobile) return;

    // 1) Find the "Search on map" trigger (robust match).
    const findMapButton = () => {
      return (
        document.querySelector("#search-on-map") ||
        document.querySelector(".search-on-map") ||
        document.querySelector("[data-testid='search-on-map']") ||
        document.querySelector("button[aria-label='Search on map']") ||
        Array.from(document.querySelectorAll("button, a, [role='button']")).find(
          (el) =>
            ((el.getAttribute("aria-label") || el.textContent || "")
              .trim()
              .toLowerCase()
              .includes("search on map"))
        )
      );
    };

    // 2) Given the button, pick the *small* card-like container that holds the map.
    const findMapCardFromButton = (btn) => {
      if (!btn) return null;

      // Fast paths if you later add a wrapper id/class:
      const fast =
        btn.closest("[data-map-preview]") ||
        btn.closest(".map-preview") ||
        btn.closest("#map-preview");
      if (fast) return fast;

      // Walk up until we find a small, card-like block (avoids fixing the whole sidebar).
      let el = btn.parentElement;
      while (el && el !== document.body) {
        const cs = window.getComputedStyle(el);
        const hasBg = cs.backgroundImage && cs.backgroundImage !== "none";
        const hasImg = !!el.querySelector("img");
        const looksCard =
          (el.className || "").toString().includes("MuiPaper-") ||
          (el.className || "").toString().includes("MuiCard-") ||
          (el.className || "").toString().includes("card") ||
          (el.className || "").toString().includes("paper");

        const w = el.offsetWidth || 0;
        const h = el.offsetHeight || 0;

        // Heuristics for a *small* map preview card
        const smallEnough = w <= 400 && h <= 350 && w >= 180 && h >= 120;

        if ((hasImg || hasBg || looksCard) && smallEnough) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    // 3) Create a single overlay root once (reused).
    let overlayRoot = document.getElementById("bf-map-overlay-root");
    if (!overlayRoot) {
      overlayRoot = document.createElement("div");
      overlayRoot.id = "bf-map-overlay-root";
      overlayRoot.style.position = "fixed";
      overlayRoot.style.left = "16px";
      overlayRoot.style.bottom = "16px";
      overlayRoot.style.zIndex = "1300";
      overlayRoot.style.pointerEvents = "none"; // container itself is transparent to clicks
      document.body.appendChild(overlayRoot);
    }

    // Keep references for cleanup/restore
    let originalParent = null;
    let originalNextSibling = null;
    let movedNode = null;
    let originalStyles = null;

    const moveCardToOverlay = () => {
      const btn = findMapButton();
      if (!btn) return false;

      const card = findMapCardFromButton(btn);
      if (!card) return false;

      // Avoid dragging huge containers by mistake
      if (card.offsetWidth > 500 || card.offsetHeight > 500) return false;

      // Save original DOM position and styles
      originalParent = card.parentNode;
      originalNextSibling = card.nextSibling;
      originalStyles = {
        margin: card.style.margin,
        width: card.style.width,
        maxWidth: card.style.maxWidth,
        boxShadow: card.style.boxShadow,
      };

      // Prepare the card to live inside the overlay (preserve width, box shadow; allow clicks)
      const rect = card.getBoundingClientRect();
      card.style.margin = "0";
      card.style.width = `${Math.max(rect.width, 220)}px`;
      card.style.maxWidth = card.style.width;
      if (!card.style.boxShadow) {
        card.style.boxShadow = "rgba(0,0,0,0.18) 0px 4px 12px";
      }

      // Allow interactions with the card content
      card.style.pointerEvents = "auto";
      overlayRoot.appendChild(card);
      movedNode = card;
      return true;
    };

    const restore = () => {
      if (movedNode && originalParent) {
        // Restore styles
        movedNode.style.margin = originalStyles.margin;
        movedNode.style.width = originalStyles.width;
        movedNode.style.maxWidth = originalStyles.maxWidth;
        movedNode.style.boxShadow = originalStyles.boxShadow;

        // Restore DOM position
        if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
          originalParent.insertBefore(movedNode, originalNextSibling);
        } else {
          originalParent.appendChild(movedNode);
        }
      }
      movedNode = null;
      originalParent = null;
      originalNextSibling = null;
      originalStyles = null;
    };

    // Try immediately; if not ready, observe.
    let didMove = moveCardToOverlay();
    let mo;

    if (!didMove) {
      mo = new MutationObserver(() => {
        if (moveCardToOverlay()) {
          didMove = true;
          mo.disconnect();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // Re-try on resize (dimensions/DOM may change)
    const onResize = () => {
      if (!movedNode) return;
      // update width lock to current size (if the source layout changed)
      const w = Math.max(movedNode.getBoundingClientRect().width, 220);
      movedNode.style.width = `${w}px`;
      movedNode.style.maxWidth = movedNode.style.width;
    };
    window.addEventListener("resize", onResize);

    // Cleanup when the page unmounts
    return () => {
      window.removeEventListener("resize", onResize);
      if (mo) mo.disconnect();
      restore();
      // Leave the overlay root in place for reuse (harmless), or remove if you prefer:
      // overlayRoot.remove();
    };
  }, [isMobile]);
  // -------------------------------------------------------------------------

  if (isLoadingStaticData) {
    return (
      <EventsLayout activeTab="events" isInIframe={isInIframe}>
        <LoadingSpinner />
      </EventsLayout>
    );
  }

  return (
    <EventsLayout activeTab="events" isInIframe={isInIframe}>
      {isMobile ? (
        <>
          <SubEventFilter
            isInIframe={isInIframe}
            gallery_event_name={galleryEventNameForAPI}
            galleryEventNameForURL={galleryEventNameForURL}
          />
          <MobileEventList
            album_name={albumNameForAPI}
            gallery_event_name={galleryEventNameForAPI}
            galleryEventNameForURL={galleryEventNameForURL}
            publicUrlPathForGalleryEvent={publicUrlPathForGalleryEvent}
          />
        </>
      ) : (
        // Desktop layout
        <Box
          sx={{
            mx: "auto",
            width: "95%",
            pt: `${TOP_PADDING_PX}px`, // top gap under the header bar
            height: VIEWPORT_HEIGHT,   // fill to bottom of screen
            display: "flex",
          }}
        >
          <Grid container spacing={2} sx={{ flex: 1, height: "100%" }}>
            {/* LEFT: Filters (sticky; scroll if long) */}
            <Grid
              item
              xs={2}
              sx={{
                alignSelf: "flex-start",
                position: "sticky",
                top: `${TOP_PADDING_PX}px`,
                maxHeight: VIEWPORT_HEIGHT,
                overflowY: "auto",
                pr: 1,
              }}
            >
              <DesktopEventPageFilter
                gallery_event_name={galleryEventNameForAPI}
                galleryEventNameForURL={galleryEventNameForURL}
                mapPlacement="bottom"
              />
            </Grid>

            {/* RIGHT: Gallery column (scroll container) */}
            <Grid
              item
              xs={10}
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pr: 1,
                  pb: 2,
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <DesktopEventList
                  album_name={albumNameForAPI}
                  gallery_event_name={galleryEventNameForAPI}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </EventsLayout>
  );
};
